import { createApp } from 'vue';
import App from './App.vue';
import { CIcon } from '@coreui/icons-vue';
import { registerSW } from 'virtual:pwa-register';
import {
  cilArrowBottom,
  cilArrowRight,
  cilArrowTop,
  cilBan,
  cilBasket,
  cilBell,
  cilCalculator,
  cilCalendar,
} from '@coreui/icons';
import * as Sentry from '@sentry/vue';
// router
import router from './router';
// store
import { createPinia } from 'pinia';
import piniaPluginPersiststate from 'pinia-plugin-persistedstate';
import { USER_STORE_KEY, ANSWER_DATES_STORE_KEY } from '@/constants/storeKeys';
// v-calendar
import { setupCalendar } from 'v-calendar';
import veProgress from "vue-ellipse-progress";
const pinia = createPinia();
pinia.use(piniaPluginPersiststate);

/**
 * URLから直接遷移する回答導線では、PWAのインストール提案が出ないように
 * Service Worker 登録を行わない。
 *
 * 想定パス（baseが `/app/` のため、実際のpathnameは `/app/...` になる）:
 * - `/login-websurvey`（登録済み被験者のURL回答）
 * - `/forms/:uuid`（リンクからの自動ログイン）
 */
const shouldRegisterServiceWorker = (): boolean => {
  const path = window.location.pathname;
  // base prefixが付いていても判定できるよう `endsWith` / regex を採用
  if (path.endsWith('/login-websurvey')) return false;
  if (/\/forms\/[^/]+$/.test(path)) return false;
  return true;
};

if ('serviceWorker' in navigator && shouldRegisterServiceWorker()) {
  registerSW({ immediate: true });
}

/** Pinia永続化データから読み取るuserストアの型定義 */
interface PersistedUserState {
  is_websurvey?: boolean;
}

/** Pinia永続化データから読み取るanswer_datesストアの型定義 */
interface PersistedAnswerDateState {
  is_auto_sign_in?: boolean;
}

/**
 * Pinia永続化データをlocalStorageから安全に読み取るヘルパー。
 * Piniaストア初期化前（beforeinstallpromptイベント内等）で使用する。
 * キー名は storeKeys.ts で一元管理し、defineStore() のストアIDと同期させている。
 */
const getPersistedStoreData = (): {
  userState: PersistedUserState;
  answerDateState: PersistedAnswerDateState;
} => {
  try {
    const userRaw = localStorage.getItem(USER_STORE_KEY);
    const answerDateRaw = localStorage.getItem(ANSWER_DATES_STORE_KEY);
    return {
      userState: userRaw ? (JSON.parse(userRaw) as PersistedUserState) : {},
      answerDateState: answerDateRaw ? (JSON.parse(answerDateRaw) as PersistedAnswerDateState) : {},
    };
  } catch {
    // パース失敗時はwebsurvey/回答リンク経由の可能性を考慮し、プロンプトを抑制する安全側に倒す
    return { userState: { is_websurvey: true }, answerDateState: { is_auto_sign_in: true } };
  }
};

/**
 * Android端末でwebsurvey・回答リンク経由のアクセス時に
 * PWAインストールプロンプト（「ホーム画面に追加」バナー）を抑制する。
 *
 * Service Worker未登録でも manifest.json + display:standalone の条件で
 * Chromeがインストール提案を出すことがあるため、beforeinstallprompt イベントで防止する。
 */
window.addEventListener('beforeinstallprompt', (e) => {
  // 初期URL判定（websurvey/回答リンクのランディングページ）
  if (!shouldRegisterServiceWorker()) {
    e.preventDefault();
    return;
  }

  // SPA内遷移後の判定: Pinia永続化データからwebsurvey/autoSignIn状態を確認
  const { userState, answerDateState } = getPersistedStoreData();
  if (userState.is_websurvey || answerDateState.is_auto_sign_in) {
    e.preventDefault();
    return;
  }
});

const app = createApp(App);
const icons = {
  cilArrowBottom,
  cilArrowRight,
  cilArrowTop,
  cilBan,
  cilBasket,
  cilBell,
  cilCalculator,
  cilCalendar,
};

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    // Vue 3 アプリのインスタンス
    app,

    // プロジェクト固有の DSN（Data Source Name）
    dsn: import.meta.env.VITE_SENTRY_DSN,

    // ヘッダ、クッキーデータを含めるかどうか
    sendDefaultPii: true,

    // 各種プラグイン（Integration）を列挙
    integrations: [
      // ルーター連携付き BrowserTracing
      Sentry.browserTracingIntegration({ router }),
      // セッションリプレイ
      Sentry.replayIntegration({
        // プライバシー保護のため、ユーザーの入力をマスク
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // トレースサンプリングレート
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'),

    // セッションリプレイのサンプリングレート
    replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE || '1.0'),

    // エラー発生時のセッションリプレイのサンプリングレート
    replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),

    // 分散トレーシング対象 URL（fetch/XHR のヘッダー伝播先）
    tracePropagationTargets: ['localhost', /^\//],
  });
}

// @TODO: 不要になったら削除
// // テスト用コード
// // --- Sentry 動作確認用の意図的エラー -----------------
// // SDK が初期化されたあと 1 tick 遅らせて投げると、
// // Vue の初期描画が完了してから落ちるのでデータが取りこぼれにくい
// setTimeout(() => {
//   // @ts-ignore
//   myUndefinedFunction();
// });

app.use(router);
app.provide('icons', icons);
app.component('CIcon', CIcon);
app.use(pinia);
app.use(veProgress);
app.mount('#app');
app.use(setupCalendar, {})
